interface TargetGroupFilters {
  gender: string[];
  age: string | string[] | null; // Can be single string or array of age ranges
  income: string[];
  affinity: string[];
}

interface TouchpointData {
  total_audience?: number;
  male_percentage?: number;
  female_percentage?: number;
  age_18_to_25_percentage?: number;
  age_26_to_35_percentage?: number;
  age_36_to_45_percentage?: number;
  age_above_45_percentage?: number;
  user_personas?: Record<string, number>;
  income_group?: Record<string, number>;
}

/**
 * Check if any target group filters are selected
 * Returns true if at least one filter (gender, age, income, or affinity) has a value
 */
export const hasFiltersSelected = (
  targetGroupFilters: TargetGroupFilters | null | undefined
): boolean => {
  if (!targetGroupFilters) return false;

  const hasGender =
    Array.isArray(targetGroupFilters.gender) &&
    targetGroupFilters.gender.length > 0;
  const hasAge =
    targetGroupFilters.age !== null &&
    targetGroupFilters.age !== '' &&
    targetGroupFilters.age !== undefined &&
    !(
      Array.isArray(targetGroupFilters.age) &&
      targetGroupFilters.age.length === 0
    );
  const hasIncome =
    Array.isArray(targetGroupFilters.income) &&
    targetGroupFilters.income.length > 0;
  const hasAffinity =
    Array.isArray(targetGroupFilters.affinity) &&
    targetGroupFilters.affinity.length > 0;

  return hasGender || hasAge || hasIncome || hasAffinity;
};

/**
 * Calculate gender percentage based on selected genders
 */
const calculateGenderPercentage = (
  selectedGenders: string[],
  touchpointData: TouchpointData
): number => {
  if (
    !selectedGenders ||
    !Array.isArray(selectedGenders) ||
    selectedGenders.length === 0
  ) {
    return 100; // No filter = 100%
  }

  let totalPercentage = 0;

  selectedGenders.forEach(gender => {
    if (gender === 'Male' && touchpointData.male_percentage) {
      totalPercentage += touchpointData.male_percentage;
    } else if (gender === 'Female' && touchpointData.female_percentage) {
      totalPercentage += touchpointData.female_percentage;
    } else if (gender === 'Other') {
      // 'Other' uses 100% (no filtering)
      totalPercentage += 100;
    }
  });

  return totalPercentage;
};

/**
 * Calculate age percentage based on selected age ranges
 * Note: Age is single selection, but we handle it as array for consistency
 */
const calculateAgePercentage = (
  selectedAge: string | string[] | null,
  touchpointData: TouchpointData
): number => {
  if (
    !selectedAge ||
    (Array.isArray(selectedAge) && selectedAge.length === 0)
  ) {
    return 100; // No filter = 100%
  }

  let totalPercentage = 0;

  // Convert to array if it's a single string
  const ageArray = Array.isArray(selectedAge) ? selectedAge : [selectedAge];

  // Sum percentages for all selected age ranges
  ageArray.forEach(age => {
    switch (age) {
      case '18-25':
        totalPercentage += Math.floor(
          touchpointData.age_18_to_25_percentage || 0
        );
        break;
      case '26-35':
        totalPercentage += Math.floor(
          touchpointData.age_26_to_35_percentage || 0
        );
        break;
      case '36-45':
        totalPercentage += Math.floor(
          touchpointData.age_36_to_45_percentage || 0
        );
        break;
      case '45+':
        totalPercentage += Math.floor(
          touchpointData.age_above_45_percentage || 0
        );
        break;
      default:
        // Unknown age range, skip
        break;
    }
  });

  // If no valid age ranges were found, return 100%
  return totalPercentage > 0 ? totalPercentage : 100;
};

/**
 * Calculate income percentage based on selected income groups
 */
const calculateIncomePercentage = (
  selectedIncome: string[],
  touchpointData: TouchpointData
): number => {
  if (
    !selectedIncome ||
    !Array.isArray(selectedIncome) ||
    selectedIncome.length === 0
  ) {
    return 100; // No filter = 100%
  }

  if (!touchpointData.income_group) {
    return 0; // No data available
  }

  let totalPercentage = 0;

  selectedIncome.forEach(income => {
    const percentage = touchpointData.income_group?.[income] || 0;
    totalPercentage += percentage;
  });

  return totalPercentage;
};

/**
 * Calculate affinity percentage based on selected user personas
 */
const calculateAffinityPercentage = (
  selectedAffinity: string[],
  touchpointData: TouchpointData
): number => {
  if (
    !selectedAffinity ||
    !Array.isArray(selectedAffinity) ||
    selectedAffinity.length === 0
  ) {
    return 100; // No filter = 100%
  }

  if (!touchpointData.user_personas) {
    return 0; // No data available
  }

  let totalPercentage = 0;

  selectedAffinity.forEach(affinity => {
    const percentage = touchpointData.user_personas?.[affinity] || 0;
    totalPercentage += percentage;
  });

  return totalPercentage;
};

/**
 * Calculate target audience based on selected filters
 * Formula: totalAudience × (genderPct/100) × (agePct/100) × (incomePct/100) × (affinityPct/100)
 */
export const calculateTargetAudience = (
  totalAudience: number,
  touchpointData: TouchpointData,
  selectedFilters: TargetGroupFilters
): number => {
  if (!totalAudience || totalAudience === 0) {
    return 0;
  }

  const genderPct = calculateGenderPercentage(
    selectedFilters.gender,
    touchpointData
  );
  const agePct = calculateAgePercentage(selectedFilters.age, touchpointData);
  const incomePct = calculateIncomePercentage(
    selectedFilters.income,
    touchpointData
  );
  const affinityPct = calculateAffinityPercentage(
    selectedFilters.affinity,
    touchpointData
  );
  // Apply compound percentage calculation
  const targetAudience =
    totalAudience *
    (genderPct / 100) *
    (agePct / 100) *
    (incomePct / 100) *
    (affinityPct / 100);

  return Math.round(targetAudience);
};
